import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase';
import LoadingSpinner from '@/shared/components/LoadingSpinner';
import { appHref } from '@/utils/appHref';

/**
 * /reactivate — the page behind the "reopen my access" link in a lapsed-trial
 * reactivation email. It sits in front of the `confirmReactivate` backend
 * callable, which grants a fresh window of access.
 *
 * The reactivation happens ONLY on an explicit button click (handleConfirm) —
 * never on mount. Email clients and security scanners prefetch links (and some
 * execute page JS); a page that reactivated on load would silently grant access
 * whenever a crawler touched the URL, burning the user's one-time reopen before
 * they ever clicked. On mount we only parse the token and show the confirm
 * screen.
 */

type ResultStatus = 'extended' | 'already' | 'ineligible' | 'invalid' | 'limit' | 'error';
type PageStatus = 'confirm' | 'loading' | 'success' | 'friendly' | 'error';

const ReactivatePage: React.FC = () => {
  const location = useLocation();
  const [status, setStatus] = useState<PageStatus>('loading');
  const [token, setToken] = useState<string>('');
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const t = urlParams.get('token');

    // Token only — no action fires here (crawler safety, see file header).
    if (!t) {
      setStatus('error');
      return;
    }

    setToken(t);
    setStatus('confirm');
  }, [location.search]);

  const handleConfirm = async () => {
    setStatus('loading');
    try {
      const res = await httpsCallable(functions, 'confirmReactivate')({ token });
      const result = ((res.data as { status?: ResultStatus })?.status) ?? 'error';

      switch (result) {
        case 'extended':
          setMessage("Your access is open again. You've got a fresh week to pick up where you left off.");
          setStatus('success');
          break;
        case 'already':
          setMessage("You're all set. Your access is already open.");
          setStatus('success');
          break;
        case 'limit':
        case 'ineligible':
          setStatus('friendly');
          break;
        case 'invalid':
        case 'error':
        default:
          setStatus('error');
          break;
      }
    } catch (_error) {
      setStatus('error');
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-neutral-600">One moment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-3xl border-2 border-neutral-200 shadow-ledge p-6">
        <div className="text-center">
          {status === 'confirm' && (
            <>
              <h2 className="font-display text-xl font-semibold text-neutral-900">Welcome back</h2>
              <p className="mt-2 text-neutral-600">
                Tap below and we'll reopen your access so you can keep studying.
              </p>
              <button
                onClick={handleConfirm}
                className="mt-6 w-full rounded-2xl bg-primary-600 px-4 py-3 font-bold text-white transition-colors hover:bg-primary-700"
              >
                Reopen my access
              </button>
              <a href={appHref('/')} className="mt-3 inline-block text-sm font-bold text-neutral-500 hover:text-neutral-700">
                Not now
              </a>
            </>
          )}

          {status === 'success' && (
            <>
              <h2 className="font-display text-xl font-semibold text-green-600">You're back in</h2>
              <p className="mt-2 text-neutral-600">{message}</p>
              <a
                href={appHref('/dashboard')}
                className="mt-6 inline-block w-full rounded-2xl bg-primary-600 px-4 py-3 font-bold text-white transition-colors hover:bg-primary-700"
              >
                Continue studying
              </a>
            </>
          )}

          {status === 'friendly' && (
            <>
              <h2 className="font-display text-xl font-semibold text-neutral-900">Let's sort this out</h2>
              <p className="mt-2 text-neutral-600">
                I couldn't reopen this automatically. Just reply to the email and Pam will sort it out.
              </p>
              <a
                href="mailto:pam@socialworktestprepacademy.com"
                className="mt-4 inline-block text-sm font-bold text-primary-600 hover:text-primary-700"
              >
                pam@socialworktestprepacademy.com
              </a>
            </>
          )}

          {status === 'error' && (
            <>
              <h2 className="font-display text-xl font-semibold text-red-600">This link didn't work</h2>
              <p className="mt-2 text-neutral-600">
                This link didn't work or has expired. Reply to the email and we'll get you back in, or
                reach us at{' '}
                <a href="mailto:pam@socialworktestprepacademy.com" className="font-bold text-primary-600">
                  pam@socialworktestprepacademy.com
                </a>
                .
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReactivatePage;
